import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';

const AppTermsOfUse = () => {
  // Check for utm=mobile query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = urlParams.get('utm') === 'mobile';
  const termsContent = `
**Effective Date:** 05/Oct/2025  
**Last Updated:** 10/Nov/2025  

## 1. Introduction & Acceptance

Welcome to MetroRahi, a mobile application and web service ("Service") developed and operated by Resiara Software Pvt. Ltd. ("Company," "we," "our," or "us"). These Terms of Use ("Terms") govern your use of the MetroRahi mobile application, website, and related services.

**By downloading, installing, accessing, or using MetroRahi, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, please do not use our Service.**

These Terms constitute a legally binding agreement between you and Resiara Software Pvt. Ltd. Your continued use of the Service constitutes acceptance of any modifications to these Terms.

### 1.1 Platform Applicability

**These Terms apply to MetroRahi mobile applications available on both:**
- **Apple iOS Platform** (distributed through Apple App Store)
- **Google Android Platform** (distributed through Google Play Store)

**By using MetroRahi on either platform, you agree that these Terms govern your use regardless of the platform, and all provisions herein apply uniformly across both iOS and Android platforms.**

---

## 2. Eligibility & User Obligations

### 2.1 Age Requirements
- You must be at least 13 years old to use MetroRahi
- Users under 18 must have parental or guardian consent
- We do not knowingly collect information from children under 13

### 2.2 User Responsibilities
You agree to:
- Provide accurate and truthful information when creating an account
- Maintain the security of your account credentials
- Use the Service only for lawful purposes
- Comply with all applicable local, national, and international laws
- Not share your account with others
- Notify us immediately of any unauthorized use of your account

---

## 3. Service Scope & Limitations

### 3.1 Service Description
MetroRahi provides:
- Metro route planning and navigation assistance
- Real-time metro information and updates
- Location-based services for metro station detection
- Journey tracking and notifications
- General metro system information

### 3.2 Service Limitations
**Important:** MetroRahi is an informational tool and may not always be 100% accurate due to:
- Technical limitations and system constraints
- External dependencies on third-party data sources
- Real-time changes in metro schedules and routes
- Network connectivity issues
- Device-specific limitations

---

## 4. Accuracy Disclaimer

### 4.1 Information Accuracy
**CRITICAL DISCLAIMER:** MetroRahi provides information for general guidance purposes only. We are not liable for:

- Incorrect or outdated metro schedules
- Delays in information updates
- Missed connections or route changes
- Financial losses due to reliance on app information
- Missed appointments or opportunities
- Any damages resulting from inaccurate information

### 4.2 User Verification Required
**You must independently verify all critical information** including:
- Metro schedules and timings
- Route availability and changes
- Station accessibility and closures
- Fare information and payment methods
- Real-time service status

---

## 5. Intellectual Property

### 5.1 Company Ownership
All rights, title, and interest in MetroRahi, including but not limited to:
- Software code, algorithms, and technical implementations
- User interface design, graphics, and visual elements
- Branding, logos, trademarks, and trade names
- Documentation, manuals, and help content
- Database structures and data organization

**Are the exclusive property of Resiara Software Pvt. Ltd.**

### 5.2 Prohibited Activities
You may not:
- Copy, modify, or distribute the Service
- Reverse engineer, decompile, or disassemble the software
- Create derivative works based on our Service
- Remove or alter any proprietary notices
- Use our trademarks or branding without permission

---

## 6. Privacy & Data Use

### 6.1 Privacy Policy
Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
You can read our full Privacy Policy at https://metrorahi.resiara.com/app/privacy_policy.

### 6.2 Data Collection
By using MetroRahi, you consent to our collection and use of:
- Personal information (name, email, profile data)
- Location data for metro station detection and navigation
- Device information for Service optimization
- Usage analytics for Service improvement
- Communication data for notifications and support

### 6.3 International Data Protection Compliance

MetroRahi complies with applicable international data protection regulations, including but not limited to:

- **General Data Protection Regulation (GDPR)** - European Union
- **California Consumer Privacy Act (CCPA)** - California, USA
- **Personal Information Protection and Electronic Documents Act (PIPEDA)** - Canada
- **Australian Privacy Principles (APP)** - Australia
- **Personal Data Protection Act (PDPA)** - Singapore and other jurisdictions
- **Information Technology Act, 2000 & IT Rules, 2011** - India

### 6.4 Data Storage Location

**All personal data collected by MetroRahi is stored securely on Amazon Web Services (AWS) cloud infrastructure located exclusively within the India region (AWS Asia Pacific - Mumbai region).**

This includes all categories of data mentioned in Section 6.2, including user account information, location data, device information, usage analytics, and communication data.

**No data is transferred or stored outside the India region without your explicit consent, except as required by law or as necessary for the provision of core services (e.g., third-party authentication services).**

### 6.5 Data Transfer Safeguards

When data must be processed by third-party services (such as Google, Apple, Mapbox, or RevenueCat), we ensure that:
- Data transfers comply with applicable data protection laws
- Third-party services maintain adequate security measures
- Data processing agreements are in place with all service providers
- Your data rights are preserved throughout the processing chain

### 6.6 International User Rights

Depending on your jurisdiction, you may have additional rights under applicable data protection laws, including the right to access, rectify, erase, restrict processing, data portability, and object to processing. To exercise these rights, please contact us at support.metrorahi@resiara.com with your request and proof of identity.

---

## 7. Payments & Subscriptions

### 7.1 Premium Features
MetroRahi offers premium features through subscription plans to cover operational costs and provide enhanced functionality. Pricing and features are subject to change with notice.
By subscribing, you agree that the purchase will be charged to your Apple ID account and will automatically renew unless canceled at least 24 hours before the end of the current period.

### 7.2 Billing & Renewal
- Subscriptions are billed through Apple App Store or Google Play Store
- Subscription management is handled via RevenueCat integration
- Automatic renewal occurs unless cancelled before the renewal date
- You can manage subscriptions through your device's app store settings
- We do not process payments directly

### 7.3 Refunds
- Refund requests must be made through Apple App Store or Google Play Store
- We follow the refund policies of the respective app stores
- Refunds are subject to the app store's terms and conditions

---

## 8. Limitation of Liability

### 8.1 General Limitation
**TO THE MAXIMUM EXTENT PERMITTED BY LAW, RESIARA SOFTWARE PVT. LTD. SHALL NOT BE LIABLE FOR:**

- **Financial Losses:** Any monetary damages, lost profits, or missed opportunities
- **Service Disruptions:** Downtime, errors, bugs, or technical failures
- **Indirect Damages:** Consequential, incidental, or punitive damages
- **Third-Party Issues:** Failures of external services or integrations
- **Data Loss:** Loss of data, information, or content
- **Security Breaches:** Unauthorized access or cyberattacks
- **Reliance Damages:** Damages from relying on Service information
- **Data-Related Claims:** Any claims, damages, or legal actions arising from data collection, storage, processing, security breaches, or data loss
- **Platform-Specific Issues:** Any issues specific to iOS or Android platforms, including but not limited to app store policies, platform restrictions, or platform-specific data handling
- **Regulatory Actions:** Any fines, penalties, or legal actions imposed by data protection authorities or regulatory bodies
- **Compliance Disputes:** Claims related to alleged non-compliance with data protection laws, platform policies, or international regulations

### 8.2 Maximum Liability
**Our total aggregate liability to you for any and all claims, damages, losses, or legal actions (including data-related claims) arising from these Terms, your use of the Service, or any data processing activities, shall not exceed the amount you paid for the Service in the 12 months preceding the claim, or ₹1,000 (Indian Rupees One Thousand), whichever is greater.**

In no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, even if we have been advised of the possibility of such damages.

### 8.3 No Warranty
**TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR DATA SECURITY.**

### 8.4 Third-Party Service Liability
**Resiara Software Pvt. Ltd. is not responsible for any issues, damages, or legal actions arising from:**
- Third-party service failures (Google, Apple, Mapbox, RevenueCat, AWS, etc.)
- Third-party data breaches or security incidents
- Platform-specific restrictions or requirements imposed by Apple or Google
- App store policies, changes, or enforcement actions
- Legal actions or regulatory penalties imposed on third-party service providers

### 8.5 Force Majeure
**We are not liable for any failures, breaches, or legal actions resulting from:**
- Cyber-attacks, hacking, or security incidents beyond our reasonable control
- Government orders, regulations, or legal requirements
- Force majeure events (natural disasters, wars, pandemics, etc.)
- Acts of third parties over which we have no control
- Platform-specific restrictions or requirements imposed by Apple or Google

---

## 9. Termination of Service

### 9.1 Termination by Company
We may suspend or terminate your access to MetroRahi immediately, without notice, if you:
- Violate these Terms or our Privacy Policy
- Engage in fraudulent or illegal activities
- Misuse the Service or compromise its security
- Fail to pay required fees (if applicable)

### 9.2 Termination by User
You may terminate your use of MetroRahi at any time by:
- Deleting the application from your device
- Cancelling any active subscriptions
- Contacting us to request account deletion

---

## 10. Updates to Terms

### 10.1 Modification Rights
We reserve the right to modify these Terms at any time. Changes will be effective when posted on our website or within the app.

### 10.2 Notification of Changes
We will notify you of material changes through:
- In-app notifications
- Email notifications (if you have provided an email)
- App store update descriptions
- Website announcements

### 10.3 Acceptance of Changes
Your continued use of MetroRahi after changes are posted constitutes acceptance of the modified Terms. If you do not agree to the changes, you must stop using the Service.

---

## 11. Governing Law & Jurisdiction

### 11.1 Applicable Law
These Terms are governed by and construed in accordance with the laws of India, without regard to conflict of law principles.

### 11.2 Jurisdiction
**Any disputes, claims, or legal actions (including data-related claims) arising from these Terms, your use of MetroRahi, or any data processing activities shall be subject to the exclusive jurisdiction of the courts in Jaipur, Rajasthan, India.**

**By using MetroRahi, you irrevocably consent to the exclusive jurisdiction of Indian courts and waive any objection to the venue of any such proceeding in such courts, regardless of your location or the platform (iOS or Android) on which you use the Service.**

### 11.3 Compliance with Indian Laws
These Terms comply with applicable Indian laws including:
- **Information Technology Act, 2000** and IT Rules, 2011
- **Consumer Protection Act, 2019**
- **Personal Data Protection Bill** (when enacted)
- **Indian Contract Act, 1872**

### 11.4 Indemnification
**By using MetroRahi, you agree to indemnify, defend, and hold harmless Resiara Software Pvt. Ltd., its directors, officers, employees, agents, and affiliates from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:**
- Your violation of these Terms or our Privacy Policy
- Your misuse of the Service
- Your violation of any applicable laws or regulations
- Any claims made by third parties related to your use of the Service
- Any data-related claims or legal actions resulting from your use of the Service

### 11.5 Release of Claims
**To the fullest extent permitted by law, you hereby release and forever discharge Resiara Software Pvt. Ltd. from any and all claims, demands, damages, losses, liabilities, and legal actions (whether known or unknown, suspected or unsuspected) arising from or related to your use of MetroRahi, data collection, storage, processing, security, or any other aspect of the Service, except as expressly provided in these Terms.**

---

## 12. Contact Information

**For questions about these Terms of Use:**

📧 **Email:** support.metrorahi@resiara.com  
🏢 **Address:** Resiara Software Pvt. Ltd., Jaipur, Rajasthan, India

**For legal notices:**
📧 **Legal Email:** support.metrorahi@resiara.com

---

---

**By using MetroRahi on iOS or Android platforms, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use, including all limitations of liability, indemnification obligations, and legal protections set forth herein. If you do not agree to these terms, you must immediately stop using the Service and delete the application from your device.**

**Last Updated:** 10/Nov/2025

**Version:** 1.0`;

  return (
    <div className="policy-page app-policy">
      <div className="policy-container">
        <div className="policy-document">
          {/* Header */}
          <motion.div
            className="policy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {!isMobile && (
              <Link to="/" className="back-button">
                <ArrowLeft className="back-icon" />
                <span>Back</span>
              </Link>
            )}
            
            <div className="policy-title-section">
              <h1>Terms of Use</h1>
              <p>MetroRahi Mobile App Terms of Use</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="policy-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="policy-text">
              {termsContent.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <h1 key={index}>{line.substring(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={index}>{line.substring(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={index}>{line.substring(4)}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="bold-text">{line.substring(2, line.length - 2)}</p>;
                } else if (line.startsWith('- ')) {
                  return <li key={index}>{line.substring(2)}</li>;
                } else if (line.trim() === '---') {
                  return <hr key={index} className="section-divider" />;
                } else if (line.trim() === '') {
                  return <br key={index} />;
                } else {
                  return <p key={index}>{line}</p>;
                }
              })}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="policy-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="company-info">
              <p>Brought to you by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link">Resiara Software Pvt. Ltd.</a></p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AppTermsOfUse;
